import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropzone, { FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import { MousePointerSquareDashed, Trash2 } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import UploadFile from 'assets/upload_file.png';
import { Button } from 'components/ui/button';

interface FileUploadProps {
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
}

export default function ImageUpload({ files, setFiles }: FileUploadProps) {
  const { t } = useTranslation();

  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const [file] = rejectedFiles;

    setIsDragOver(false);

    toast.error(`${file.file.type} ${t('type_not_supported')}.`);
  };

  const onDropAccepted = (acceptedFiles: File[]) => {
    // const [acceptedFile] = acceptedFiles;
    setFiles(acceptedFiles);

    setIsDragOver(false);
  };

  return (
    <div>
      <Dropzone
        onDropRejected={onDropRejected}
        onDropAccepted={onDropAccepted}
        accept={{
          'image/jpeg': ['.jpeg', '.jpg'],
          'image/png': ['.png'],
        }}
        onDragEnter={() => setIsDragOver(true)}
        onDragLeave={() => setIsDragOver(false)}
        data-testid='dropzone'
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            className={cn(
              'flex flex-col items-center justify-center w-full border border-dashed rounded-lg h-48 border-secondary',
              isDragOver && 'bg-secondary/10'
            )}
          >
            <input {...getInputProps()} />
            {isDragOver ? (
              <>
                <MousePointerSquareDashed className='mb-2 size-8 text-secondary' />
                <p className='font-medium'>
                  <span className='text-secondary'>Drop file</span> to upload
                </p>
              </>
            ) : (
              <>
                <div className='size-20'>
                  <img
                    src={UploadFile}
                    alt='upload file'
                    className='object-cover w-full h-full'
                  />
                </div>
                <div className='mt-2 font-medium'>
                  <span className='underline cursor-pointer text-secondary underline-offset-2'>
                    {t('click_to_upload')}
                  </span>{' '}
                  {t('or_dnd')}
                </div>
                <p className='text-sm font-medium text-gray-600'>JPEG, PNG</p>
              </>
            )}
          </div>
        )}
      </Dropzone>

      {files?.length ? (
        <div className='mt-4'>
          {files.map((file) => (
            <div className='flex items-center justify-between' key={file.name}>
              <div className='flex items-center gap-x-2'>
                {/* <File className='size-6 text-secondary' /> */}
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className='object-fill h-16 overflow-hidden rounded-xl aspect-square'
                />
                <div className='flex flex-col items-start'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className='w-32 truncate sm:w-60 text-start'>
                        {file?.name}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{file?.name}</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className='leading-none'>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>

              <Button
                variant='ghost'
                className='rounded-sm'
                onClick={() =>
                  setFiles((files) => files.filter((f) => f !== file))
                }
              >
                <Trash2 className='text-rose-500 size-6' />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
